import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useWeekStartDay } from 'modules/common/hooks/useWeekStartDay';
import { getCurrentWeekDateRange } from './hooks/useNoteInsightsDateRange';

export enum NoteInsightsViewOptions {
  CUSTOM = 'CUSTOM',
  THIS_MONTH = 'THIS_MONTH',
  TODAY = 'TODAY',
  THIS_WEEK = 'THIS_WEEK',
  LAST_NINETY_DAYS = 'LAST_NINETY_DAYS'
}

type Period = {
  endDate: string;
  startDate: string;
};

type NoteInsightsContextValue = {
  currentPeriod: Period | null;
  setCurrentPeriod: (period: Period) => void;
  selectedViewOption: NoteInsightsViewOptions;
  setSelectedViewOption: (selectedViewOption: NoteInsightsViewOptions) => void;
};

export const NoteInsightsContext = createContext<NoteInsightsContextValue | null>(
  null
);

export const useNoteInsightsContext = (): NoteInsightsContextValue => {
  const value = useContext(NoteInsightsContext);
  if (!value) {
    throw Error('should be called in NoteInsightsContextProvider scope');
  }
  return value;
};

export type NoteInsightsContextProviderType = {
  children?: React.ReactNode;
};

const NoteInsightsContextProvider: React.FC<NoteInsightsContextProviderType> = ({
  children
}: NoteInsightsContextProviderType) => {
  const weekStart = useWeekStartDay();
  const { startDate, endDate } = getCurrentWeekDateRange(weekStart);
  const [currentPeriod, setCurrentPeriod] = useState<Period | null>({
    startDate: startDate,
    endDate: endDate
  });

  const [selectedViewOption, setSelectedOption] = useState(
    NoteInsightsViewOptions.THIS_WEEK
  );

  const setSelectedViewOption = useCallback(
    (selectedViewOption: NoteInsightsViewOptions) => {
      setSelectedOption(selectedViewOption);
    },
    []
  );

  const value = useMemo(
    () => ({
      currentPeriod,
      setCurrentPeriod,
      selectedViewOption,
      setSelectedViewOption: setSelectedViewOption
    }),
    [currentPeriod, selectedViewOption, setSelectedViewOption]
  );
  return (
    <NoteInsightsContext.Provider value={value}>
      {children}
    </NoteInsightsContext.Provider>
  );
};

export default NoteInsightsContextProvider;
